/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useRef, useCallback} from 'react'
import Cookies from 'js-cookie'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {getAssetUrl} from 'pwa-kit-react-sdk/ssr/universal/utils'
import StorefrontPreview from 'pwa-kit-react-sdk/storefront-preview'

// Chakra
import {Box, useDisclosure, useStyleConfig} from '@chakra-ui/react'
import {SkipNavLink, SkipNavContent} from '@chakra-ui/skip-nav'
import {useCommerceAPI} from '../../commerce-api/contexts'
import useAsync from '../../commerce-api/hooks/useAsync'
import useOcapiSite from '../../commerce-api/hooks/useOcapiSite'

// Contexts
import {
    CurrencyProvider,
    HeaderContentProvider,
    HeaderProvider,
    NonceProvider
} from '../../contexts'

// Local Project Components
import {useGeoIPModal, CountrySelectorModal} from '../../components/country-selector-modal'
import Header from '../../components/header'
import OfflineBanner from '../../components/offline-banner'
import OfflineBoundary from '../../components/offline-boundary'
import ScrollToTop from '../../components/scroll-to-top'
import Footer from '../../components/footer'
import {
    preloadCmsContent,
    TYPE_CONTENT_SLOT
} from '../../components/cms-content/preload-cms-content'
import CheckoutHeader from '../../pages/checkout/partials/checkout-header'
import CheckoutFooter from '../../pages/checkout/partials/checkout-footer'
import CMSGlobalStyles from '../cms-content/global-styles'
import DrawerMenu from '../drawer-menu'

// Hooks
import useShopper from '../../commerce-api/hooks/useShopper'
import useCustomer from '../../commerce-api/hooks/useCustomer'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import {AddToCartModalProvider} from '../../hooks/use-add-to-cart-modal'
import {BonusProductsModalProvider} from '../../hooks/use-bonus-products-modal'
import useWishlist from '../../hooks/use-wishlist'

// Localization
import {IntlProvider} from 'react-intl'

// Others
import {watchOnlineStatus} from '../../utils/utils'

import {getTargetLocale, fetchTranslations} from '../../utils/locale'
import {
    DEFAULT_SITE_TITLE,
    HOME_HREF,
    CART_HREF,
    THEME_COLOR,
    DEFAULT_LOCALE,
    ACCOUNT_HREF,
    HYBRID_HOME,
    HYBRID_CART,
    HYBRID_ACCOUNT
} from '../../constants'

import Seo from '../seo'
import CookieBot from '../cookiebot'
import {EmplifiProvider} from '../emplifi'
import Gtm from '../../gtm-api/gtm'
import {resolveSiteFromUrl} from '../../utils/site-utils'
import useMultiSite from '../../hooks/use-multi-site'
import NewsletterBanner from '../newsletter-banner'
import {ConvertAPIProvider} from '../../convert-api'
import useHybridURL from '../../hooks/use-hybrid-url'
import {ExposedProductQuickviewModal} from '../product-quickview-modal'
import useEffectEvent from '../../hooks/use-effect-event'
import {KindlyChatProvider} from '../kindly-chat'
import EmplifiKindlyIntegration from '../emplifi-kindly-integration'
import HeaderNavigation from '../header/header-navigation'
import {getConfig} from 'pwa-kit-runtime/utils/ssr-config'

const App = (props) => {
    const {
        children,
        targetLocale = DEFAULT_LOCALE,
        messages,
        headerNavigationLinks = [],
        nonce,
        ocapiSite,
        footerContent,
        checkoutFooterContent,
        slotContent,
        disableGtm
    } = props
    const {app: appConfig} = getConfig()

    const headerRef = useRef()
    const location = useLocation()
    const authModal = useAuthModal()
    const customer = useCustomer()
    const {site, locale} = useMultiSite()
    const {googleVerificationTag} = useOcapiSite()
    const navigateHybrid = useHybridURL()
    const countryAndLocaleCookie = Cookies.get('dw_country_and_locale')
    const {onOpen: onGeoIpModal, ...geoIpModalProps} = useGeoIPModal()

    const [isOnline, setIsOnline] = useState(true)
    const styles = useStyleConfig('App')

    const {isOpen, onOpen, onClose: _onClose} = useDisclosure()
    const [selectedLvl0NavigationItemId, setSelectedLvl0NavigationItemId] = useState(null)
    const onClose = useCallback(() => {
        setSelectedLvl0NavigationItemId(null)
        _onClose()
    }, [_onClose])
    const onNavigationShortcutClick = useCallback(
        (id) => {
            if (selectedLvl0NavigationItemId === id) {
                onClose()
            } else {
                setSelectedLvl0NavigationItemId(id)
                // open navigation
                onOpen()
            }
        },
        [selectedLvl0NavigationItemId, onOpen, onClose]
    )

    // Used to conditionally render header/footer for cancel order & checkout pages
    const isCheckout = /\/appcheckout$/.test(location?.pathname)
    const isCancelOrder = /\/cancel-order/.test(location?.pathname)
    const isCheckoutConfirmation = /\/appcheckout\/confirmation$/.test(location?.pathname)
    const isCart = /\/appcart$/.test(location?.pathname)
    const isHome = /\/home$/.test(location?.pathname)

    const isCheckOrConfirmationOrCancelOrder = isCheckout || isCheckoutConfirmation || isCancelOrder

    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = locale.preferredCurrency || l10n.defaultCurrency

    // fetch geoIp data
    const api = useCommerceAPI()
    const {
        execute: fetchGeoIp,
        data: geoIPData,
        error: geoIpError,
        loading: geoIpLoading
    } = useAsync(() => api.sapi.getGeoIpData())
    useEffect(() => {
        if (!countryAndLocaleCookie) {
            fetchGeoIp()
        }
    }, [countryAndLocaleCookie, fetchGeoIp])

    const onShowGeoIpModal = useEffectEvent((geoIPData) => {
        onGeoIpModal(geoIPData)
    })
    useEffect(() => {
        if (
            !countryAndLocaleCookie &&
            !geoIpLoading &&
            !geoIpError &&
            geoIPData &&
            geoIPData.locale !== targetLocale.replace('-', '_')
        ) {
            onShowGeoIpModal(geoIPData)
        }
    }, [
        countryAndLocaleCookie,
        geoIpLoading,
        geoIpError,
        geoIPData,
        onShowGeoIpModal,
        targetLocale
    ])

    // Set up ocapi site
    useOcapiSite(ocapiSite)
    const {croConvertEnabled, croConvertScriptUrl} = ocapiSite?.custom?.c_result?.preferences || {}

    // Set up customer and basket
    useShopper({currency})

    const wishlist = useWishlist()
    const onCustomerTypeChange = useEffectEvent(() => {
        if (!customer.isInitialized) {
            return
        }
        if (customer.isRegistered) {
            wishlist.init()
        }
        if (customer.isGuest) {
            wishlist.reset()
        }
    })
    useEffect(() => {
        onCustomerTypeChange(customer.authType)
    }, [customer.authType, onCustomerTypeChange])

    useEffect(() => {
        // Listen for online status changes.
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })
    }, [])

    const onLocationChange = useEffectEvent(() => {
        // Lets automatically close the mobile navigation when the
        // location path is changed.
        onClose()
    })
    useEffect(() => {
        onLocationChange(location)
    }, [location, onLocationChange])

    const onLogoClick = () => {
        // Goto the home page.
        navigateHybrid(HYBRID_HOME, HOME_HREF)
        // Close the drawer.
        onClose()
    }

    const onCartClick = () => {
        navigateHybrid(HYBRID_CART, CART_HREF)
        // Close the drawer.
        onClose()
    }

    const onAccountClick = () => {
        // Link to account page for registered customer, open auth modal otherwise
        if (customer.isRegistered) {
            navigateHybrid(HYBRID_ACCOUNT, ACCOUNT_HREF)
        } else {
            // if they already are at the login page, do not show login modal
            if (new RegExp(`^/login$`).test(location.pathname)) return
            authModal.onOpen()
        }
        // Close the drawer.
        onClose()
    }

    const getToken = useCallback(() => {
        if (!api.auth.isTokenValid) {
            return api.auth.login().then(() => {
                return api.auth.authToken
            })
        }
        return api.auth.authToken
    }, [api.auth])

    return (
        <NonceProvider nonce={nonce}>
            <ConvertAPIProvider
                convertScriptUrl={croConvertScriptUrl}
                disabled={!croConvertEnabled}
            >
                <Box className="sf-app" {...styles.container}>
                    {/* For pwa-kit v2, we need to use this getToken prop to make sure it can work properly
                        with Storefront Preview feature*/}
                    <StorefrontPreview getToken={getToken}>
                        <IntlProvider
                            onError={(err) => {
                                if (err.code === 'MISSING_TRANSLATION') {
                                    // NOTE: Disable the console error for missing translations
                                    // as this is flooding the logecenter.
                                    // TODO make this configurable
                                    // if (process.env.NODE_ENV === 'production') {
                                    //     console.warn('Missing translation', err.message)
                                    // }
                                    return
                                }
                                throw err
                            }}
                            locale={targetLocale}
                            messages={messages}
                            // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
                            // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
                            // - "extract-default-translations"
                            // - "compile-translations:pseudo"
                            defaultLocale={DEFAULT_LOCALE}
                        >
                            <CurrencyProvider currency={currency}>
                                <HeaderProvider header={headerRef}>
                                    <Seo
                                        noIndex={appConfig.seo?.noIndex}
                                        htmlAttributes={{lang: locale.id}}
                                    >
                                        <meta name="theme-color" content={THEME_COLOR} />
                                        <meta
                                            name="apple-mobile-web-app-title"
                                            content={DEFAULT_SITE_TITLE}
                                        />
                                        {isHome && googleVerificationTag && (
                                            <meta
                                                name="google-site-verification"
                                                content={googleVerificationTag}
                                            />
                                        )}
                                        <link
                                            rel="apple-touch-icon"
                                            href={getAssetUrl(
                                                'static/img/global/apple-touch-icon.png'
                                            )}
                                        />
                                        <link
                                            rel="icon"
                                            type="image/png"
                                            sizes="32x32"
                                            href={getAssetUrl(
                                                'static/img/global/favicon-32x32.png'
                                            )}
                                        />
                                        <link
                                            rel="icon"
                                            type="image/png"
                                            sizes="16x16"
                                            href={getAssetUrl(
                                                'static/img/global/favicon-16x16.png'
                                            )}
                                        />
                                        <link
                                            rel="manifest"
                                            href={getAssetUrl('static/manifest.json')}
                                        />
                                        <link
                                            rel="mask-icon"
                                            href={getAssetUrl(
                                                'static/img/global/safari-pinned-tab.svg'
                                            )}
                                            color="#ED7703"
                                        />
                                    </Seo>

                                    <ScrollToTop />

                                    <KindlyChatProvider>
                                        <Box
                                            id="app"
                                            display="flex"
                                            flexDirection="column"
                                            flex={1}
                                        >
                                            <SkipNavLink zIndex="skipLink">
                                                Skip to Content
                                            </SkipNavLink>

                                            <Box {...styles.headerWrapper}>
                                                {!isCheckOrConfirmationOrCancelOrder ? (
                                                    <Header
                                                        ref={headerRef}
                                                        onMenuClick={onOpen}
                                                        onLogoClick={onLogoClick}
                                                        onMyCartClick={onCartClick}
                                                        onMyAccountClick={onAccountClick}
                                                        onMyAccountMenuOpen={onClose}
                                                        onSearchClick={onClose}
                                                    >
                                                        <DrawerMenu
                                                            selected={selectedLvl0NavigationItemId}
                                                            setSelected={
                                                                setSelectedLvl0NavigationItemId
                                                            }
                                                            isOpen={isOpen}
                                                            onClose={onClose}
                                                            onMyAccountClick={onAccountClick}
                                                        />

                                                        <HeaderNavigation
                                                            navigation={headerNavigationLinks}
                                                            selected={selectedLvl0NavigationItemId}
                                                            onClick={onNavigationShortcutClick}
                                                        />
                                                    </Header>
                                                ) : (
                                                    <CheckoutHeader
                                                        isCheckoutConfirmation={
                                                            isCheckoutConfirmation || isCancelOrder
                                                        }
                                                    />
                                                )}
                                            </Box>

                                            {!isOnline && <OfflineBanner />}
                                            <AddToCartModalProvider>
                                                <BonusProductsModalProvider>
                                                    <SkipNavContent
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            flex: 1,
                                                            outline: 0
                                                        }}
                                                    >
                                                        <Box
                                                            as="main"
                                                            id="app-main"
                                                            role="main"
                                                            display="flex"
                                                            flexDirection="column"
                                                            flex="1"
                                                        >
                                                            <OfflineBoundary isOnline={false}>
                                                                <HeaderContentProvider
                                                                    value={slotContent}
                                                                >
                                                                    {children}
                                                                </HeaderContentProvider>
                                                            </OfflineBoundary>
                                                        </Box>
                                                    </SkipNavContent>

                                                    {!isCheckOrConfirmationOrCancelOrder &&
                                                    !isCart ? (
                                                        <>
                                                            <NewsletterBanner source="Web - Banner" />
                                                            <Footer footerContent={footerContent} />
                                                        </>
                                                    ) : (
                                                        <CheckoutFooter
                                                            footerContent={checkoutFooterContent}
                                                        />
                                                    )}

                                                    <AuthModal {...authModal} />
                                                    {/* Need to be inside AddToCartModalProvider */}
                                                    <ExposedProductQuickviewModal />
                                                </BonusProductsModalProvider>
                                            </AddToCartModalProvider>
                                        </Box>

                                        {/* Keep at the end of the page to allow more critical resources to load faster */}
                                        <CMSGlobalStyles />
                                        <Gtm disabled={disableGtm} />
                                        <CookieBot />
                                        <EmplifiProvider>
                                            <EmplifiKindlyIntegration />
                                        </EmplifiProvider>
                                    </KindlyChatProvider>

                                    {!isCheckOrConfirmationOrCancelOrder &&
                                        !isCart &&
                                        geoIPData && <CountrySelectorModal {...geoIpModalProps} />}
                                </HeaderProvider>
                            </CurrencyProvider>
                        </IntlProvider>
                    </StorefrontPreview>
                </Box>
            </ConvertAPIProvider>
        </NonceProvider>
    )
}

App.shouldGetProps = () => {
    // In this case, we only want to fetch data for the app once, on the server.
    return typeof window === 'undefined'
}

App.getProps = async ({api, req, res}) => {
    const site = resolveSiteFromUrl(res.locals.originalUrl)
    const l10nConfig = site.l10n
    const targetLocale = getTargetLocale({
        getUserPreferredLocales: () => {
            // CONFIG: This function should return an array of preferred locales. They can be
            // derived from various sources. Below are some examples of those:
            //
            // - client side: window.navigator.languages
            // - the page URL they're on (example.com/en-GB/home)
            // - cookie (if their previous preference is saved there)
            //
            // If this function returns an empty array (e.g. there isn't locale in the page url),
            // then the app would use the default locale as the fallback.

            // NOTE: Your implementation may differ, this is just what we did.
            //
            // Since the CommerceAPI client already has the current `locale` set,
            // we can use it's value to load the correct messages for the application.
            // Take a look at the `app/components/_app-config` component on how the
            // preferred locale was derived.
            const {locale} = api.getConfig()

            return [locale]
        },
        l10nConfig
    })

    // run in parallel to speed up the processing
    const [footerContent, checkoutFooterContent, messages, getNavigationHeader, slotContent] =
        await Promise.all([
            // Preload footer content and provide it to Footer component
            api.shopperContent.getContent({
                parameters: {cid: 'pwa_footer'}
            }),
            // Preload checkout footer content and provide it to CheckoutFooter component
            api.shopperContent.getContent({
                parameters: {cid: 'pwa_footer_checkout'}
            }),
            fetchTranslations(targetLocale),
            // fetch navigation header data
            api.customAPIs.navigationHeader({}),
            // preload header-content slot
            preloadCmsContent('header-content', {api}, TYPE_CONTENT_SLOT),
            // Login as `guest` to get session.
            api.auth.login(),
            // run middleware as universal module
            (async () => {
                const {default: middleware} = await import('../../middleware')
                await middleware(req, res)
            })()
        ])

    const headerNavigationLinks = !getNavigationHeader.isError
        ? getNavigationHeader.items
            ? getNavigationHeader.items
            : []
        : []

    return {
        targetLocale,
        messages,
        headerNavigationLinks,
        config: res?.locals?.config,
        nonce: res?.locals?.nonce,
        ocapiSite: res?.locals?.ocapiSite,
        footerContent,
        checkoutFooterContent,
        slotContent,
        disableGtm: res?.locals?.disableGtm
    }
}

App.propTypes = {
    children: PropTypes.node,
    targetLocale: PropTypes.string,
    messages: PropTypes.object,
    headerNavigationLinks: PropTypes.array,
    config: PropTypes.object,
    nonce: PropTypes.string,
    ocapiSite: PropTypes.object,
    footerContent: PropTypes.object,
    checkoutFooterContent: PropTypes.object,
    slotContent: PropTypes.object,
    disableGtm: PropTypes.bool
}

export default App
